import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { IData } from '@core/interfaces/common/IData';
import { NotificacionesCiudadanoResultDto } from '@core/interfaces/notificaciones/notificaciones-ciudadano';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private API_URL = environment.api_url;

  private notificacionesSubject = new BehaviorSubject<IData<NotificacionesCiudadanoResultDto[]>>({
    data: [],
    pagination: {
      total: 0,
      total_pages: 0,
      page: 1,
      size: 100,
    },
  });
  public notificaciones$ = this.notificacionesSubject.asObservable();

  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  constructor(private http: HttpClient) {}

  public getUnreadCount(): number {
    return this.unreadCountSubject.value;
  }

  public loadUnreadCount(): Observable<IData<NotificacionesCiudadanoResultDto[]>> {
    return this.http
      .get<IData<NotificacionesCiudadanoResultDto[]>>(`${this.API_URL}/api/v1/mensajes-ciudadano`, {
        params: {
          mensaje_leido: false,
          page: 1,
          size: 1,
        },
      })
      .pipe(
        tap((response) => {
          if (response && response.pagination) {
            this.unreadCountSubject.next(response.pagination.total);
          }
        }),
      );
  }

  public decrementUnreadCount(): void {
    const currentCount = this.unreadCountSubject.value;
    if (currentCount > 0) {
      this.unreadCountSubject.next(currentCount - 1);
    }
  }

  public getNotificacionesCiudadano(
    mensaje_leido?: boolean,
    page: number = 1,
    size: number = 100,
  ): Observable<IData<NotificacionesCiudadanoResultDto[]>> {
    return this.http
      .get<IData<NotificacionesCiudadanoResultDto[]>>(`${this.API_URL}/api/v1/mensajes-ciudadano`, {
        params: {
          ...(mensaje_leido !== undefined ? { mensaje_leido } : {}),
          page,
          size,
        },
      })
      .pipe(
        tap((response) => {
          if (response && response.data) {
            this.notificacionesSubject.next(response);
          }
        }),
      );
  }

  public marcarNotificacionComoLeidaCiudadano(
    solicitud_notificacion_mensaje_id: number,
  ): Observable<NotificacionesCiudadanoResultDto> {
    return this.http
      .put<NotificacionesCiudadanoResultDto>(`${this.API_URL}/api/v1/notificaciones/actualizar`, {
        solicitud_notificacion_mensaje_id,
        mensaje_leido: true,
        fecha_mensaje_leido: new Date(),
      })
      .pipe(
        tap(() => {
          const notificaciones = this.notificacionesSubject.value;
          const notificacionIndex = notificaciones.data.findIndex(
            (n) => n.solicitud_notificacion_mensaje_id === solicitud_notificacion_mensaje_id,
          );
          if (notificacionIndex !== -1) {
            notificaciones.data[notificacionIndex].mensaje_leido = true;
            notificaciones.data[notificacionIndex].fecha_mensaje_leido = new Date();
            this.notificacionesSubject.next(notificaciones);
          }
        }),
      );
  }

  public eliminarNotificacionCiudadano(
    solicitud_notificacion_mensaje_id: number,
  ): Observable<NotificacionesCiudadanoResultDto> {
    return this.http
      .put<NotificacionesCiudadanoResultDto>(`${this.API_URL}/api/v1/notificaciones/actualizar`, {
        solicitud_notificacion_mensaje_id,
        mensaje_eliminado: true,
        fecha_mensaje_eliminado: new Date(),
      })
      .pipe(
        tap(() => {
          const notificaciones = this.notificacionesSubject.value;
          const notificacionIndex = notificaciones.data.findIndex(
            (n) => n.solicitud_notificacion_mensaje_id === solicitud_notificacion_mensaje_id,
          );
          if (notificacionIndex !== -1) {
            notificaciones.data[notificacionIndex].mensaje_eliminado = true;
            notificaciones.data[notificacionIndex].fecha_mensaje_eliminado = new Date();
            this.notificacionesSubject.next(notificaciones);
          }
        }),
      );
  }
}
